import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MapPin, Calendar, Users, DollarSign, Clock, CheckCircle, AlertCircle } from 'lucide-react';

export const GameBoardPage = () => {
  const { api, user } = useAuth();
  const [gameBoard, setGameBoard] = useState({ available: [], assigned: [] });
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);

  const fetchGameBoard = async () => {
    try {
      const response = await api().get('/games/board');
      setGameBoard(response.data);
    } catch (error) {
      console.error('Failed to fetch game board:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGameBoard();
    const interval = setInterval(fetchGameBoard, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [api]);

  const handleRequestGame = async (gameId) => {
    setRequesting(gameId);
    try {
      await api().post(`/games/${gameId}/request`);
      toast.success('Game request submitted! Waiting for admin approval.');
      fetchGameBoard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request game');
    } finally {
      setRequesting(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isReferee = user?.role === 'referee';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="game-board-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Game Board</h1>
        <p className="text-muted-foreground mt-1">
          {isReferee ? 'Request games and view your court assignments' : 'View all scheduled games and assignments'}
        </p>
      </div>

      {/* Available Games */}
      <div>
        <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4 flex items-center gap-2">
          <div className="w-3 h-3 bg-emerald-500 rounded-full" />
          Available Games
        </h2>
        
        {gameBoard.available.length === 0 ? (
          <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
            <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">No available games at the moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gameBoard.available.map((game) => (
              <div
                key={game.id}
                data-testid={`available-game-${game.id}`}
                className="bg-zinc-900/50 border border-emerald-900/50 p-5 hover:border-emerald-700 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading font-bold">{game.tournament_event}</h3>
                    <p className="text-sm text-zinc-400">{game.division}</p>
                  </div>
                  <span className="px-2 py-1 bg-emerald-900/30 text-emerald-400 text-xs font-bold uppercase rounded-sm">
                    Open
                  </span>
                </div>
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Calendar className="w-4 h-4" />
                    {formatDate(game.game_date)}
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <MapPin className="w-4 h-4" />
                    {game.location}
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Users className="w-4 h-4" />
                    {game.assigned_count} / {game.officials_needed} Officials
                  </div>
                  {game.court_number && (
                    <div className="flex items-center gap-2 text-blue-400">
                      <AlertCircle className="w-4 h-4" />
                      Court: {game.court_number}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-emerald-400">
                    <DollarSign className="w-4 h-4" />
                    ${game.rate}/game
                  </div>
                </div>
                
                {isReferee && (
                  <Button
                    onClick={() => handleRequestGame(game.id)}
                    disabled={requesting === game.id}
                    data-testid={`request-game-${game.id}`}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm"
                  >
                    {requesting === game.id ? 'Requesting...' : 'Request This Game'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assigned Games */}
      <div>
        <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-blue-400" />
          Assigned Games
        </h2>
        
        {gameBoard.assigned.length === 0 ? (
          <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">No games have been assigned yet</p>
          </div>
        ) : (
          <div className="bg-zinc-900/50 border border-zinc-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800 text-xs uppercase tracking-wider text-zinc-400">
                <tr>
                  <th className="px-4 py-3 text-left">Event</th>
                  <th className="px-4 py-3 text-left">Date & Time</th>
                  <th className="px-4 py-3 text-left">Location</th>
                  <th className="px-4 py-3 text-left">Court</th>
                  <th className="px-4 py-3 text-left">Assigned Officials</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {gameBoard.assigned.map((game) => (
                  <tr key={game.id} data-testid={`assigned-game-${game.id}`} className="hover:bg-zinc-800/50">
                    <td className="px-4 py-4">
                      <p className="font-medium">{game.tournament_event}</p>
                      <p className="text-sm text-zinc-500">{game.division}</p>
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-400">
                      {formatDate(game.game_date)}
                    </td>
                    <td className="px-4 py-4 text-sm text-zinc-400">
                      {game.location}
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 bg-blue-900/30 text-blue-400 text-sm font-mono rounded">
                        {game.court_number || 'TBD'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {game.assigned_referees?.map((name, i) => (
                          <span key={i} className="px-2 py-0.5 bg-zinc-800 text-xs rounded">
                            {name}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
